import {
  ArrowLeft,
  Captions,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Loader2,
  Trash2,
  Volume2,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { LiveBadge } from '../components/teamChat/VoiceChannelRow';
import { useCallDuration, VOICE_CALLS_PATH } from '../components/teamChat/VoiceCallHistory';
import { transcriptToText, VoiceTranscript } from '../components/teamChat/VoiceTranscript';
import { Avatar } from '../components/tasks/linear/Avatar';
import { useConfirm } from '../components/ui/ConfirmProvider';
import { useI18n } from '../contexts/I18nContext';
import { useDeleteVoiceCall, useLiveTranscript, useVoiceCall } from '../hooks/useVoiceCalls';
import { usePermissions } from '../hooks/usePermissions';
import { VoiceCallTaskSuggestions } from '../components/teamChat/VoiceCallTaskSuggestions';
import { VoiceCallMinutes } from '../components/teamChat/VoiceCallMinutes';
import { useAuthStore } from '../store/useAuthStore';

const HEADER_BUTTON =
  'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors disabled:opacity-50';

const dateFormat = new Intl.DateTimeFormat('ro-RO', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * /team-chat/calls/:callId — one call from the history: who was there, how
 * long it ran, and everything that was said. For a call still in progress the
 * transcript keeps growing and starts open; for a finished one it is the raw
 * record, folded away by default (the minutes below say what mattered). Either
 * way it folds and unfolds on request — followed by the minutes of the discussion (written
 * when someone asks for them) and the tasks the AI drew out of it, waiting
 * for someone to accept or ignore them.
 */
export function VoiceCallPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { callId = '' } = useParams<{ callId: string }>();
  const me = useAuthStore((s) => s.user);
  const { can } = usePermissions();
  const duration = useCallDuration();
  const { data: call, isLoading } = useVoiceCall(callId || undefined);
  const remove = useDeleteVoiceCall();
  const [copied, setCopied] = useState(false);
  // null = the default for the call's state: open while live, folded after.
  const [transcriptChoice, setTranscriptOpen] = useState<boolean | null>(null);

  const live = call?.endedAt === null;
  const transcriptOpen = transcriptChoice ?? live;
  const { segments } = useLiveTranscript({
    callId: call?.id ?? null,
    active: Boolean(live),
    initial: call?.segments,
  });

  if (!call) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent">
          {isLoading ? (
            <Loader2 size={26} className="animate-spin text-muted-foreground" />
          ) : (
            <Volume2 size={26} className="text-muted-foreground" />
          )}
        </span>
        {!isLoading && (
          <>
            <p className="max-w-sm text-sm text-muted-foreground">
              {t('teamChat.voiceCallNotFound')}
            </p>
            <Link
              to={VOICE_CALLS_PATH}
              className="text-sm font-medium text-primary hover:underline"
            >
              {t('teamChat.voiceCallBack')}
            </Link>
          </>
        )}
      </div>
    );
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(transcriptToText(segments));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked (insecure context / permissions). The button simply
      // does nothing visible; the text is still on screen to select.
    }
  };

  const del = async () => {
    const yes = await confirm({
      title: t('teamChat.voiceCallDelete'),
      message: t('teamChat.voiceCallConfirmDelete', {
        date: dateFormat.format(new Date(call.startedAt)),
      }),
      confirmLabel: t('common.delete'),
      danger: true,
    });
    if (!yes) return;
    remove.mutate(call.id, { onSuccess: () => navigate(VOICE_CALLS_PATH) });
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <Link
          to={VOICE_CALLS_PATH}
          title={t('teamChat.voiceCallBack')}
          className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <ArrowLeft size={18} />
        </Link>
        <Volume2
          size={18}
          className={live ? 'shrink-0 text-emerald-500' : 'shrink-0 text-muted-foreground'}
        />
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-2 truncate font-heading text-base font-semibold text-foreground">
            <span className="truncate">{call.channelName}</span>
            {live && <LiveBadge />}
          </h1>
          <p className="truncate text-xs text-muted-foreground">
            {dateFormat.format(new Date(call.startedAt))} · {duration(call.durationMs)}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => void copy()}
            disabled={segments.length === 0}
            title={t('teamChat.voiceCallCopy')}
            className={`${HEADER_BUTTON} text-muted-foreground hover:bg-accent hover:text-foreground`}
          >
            {copied ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
            <span className="hidden sm:inline">
              {copied ? t('teamChat.voiceCallCopied') : t('teamChat.voiceCallCopy')}
            </span>
          </button>
          {me?.role === 'ADMIN' && (
            <button
              type="button"
              onClick={() => void del()}
              disabled={remove.isPending}
              title={t('teamChat.voiceCallDelete')}
              className={`${HEADER_BUTTON} text-red-600 hover:bg-red-500/15`}
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </header>

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4">
        <section>
          <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
            {t('teamChat.voiceCallParticipants')}
          </h2>
          <ul className="flex flex-wrap gap-2">
            {call.participants.map((p) => (
              <li
                key={p.userId}
                className="flex items-center gap-2 rounded-full border border-border bg-card py-1 pl-1 pr-3 text-sm"
              >
                <Avatar name={p.name} size={22} />
                <span className="text-foreground">
                  {p.userId === me?.id ? t('teamChat.voiceYou') : p.name}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section>
          <div className="mb-2 flex items-center justify-between gap-2">
            <h2 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
              <Captions size={14} />
              {live ? t('teamChat.voiceTranscriptLive') : t('teamChat.voiceTranscript')}
              {!live && segments.length > 0 && (
                <span className="font-normal normal-case tracking-normal">
                  · {t('teamChat.voiceTranscriptCount', { count: segments.length })}
                </span>
              )}
            </h2>
            <button
              type="button"
              onClick={() => setTranscriptOpen(!transcriptOpen)}
              aria-expanded={transcriptOpen}
              className={`${HEADER_BUTTON} text-muted-foreground hover:bg-accent hover:text-foreground`}
            >
              {transcriptOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              {transcriptOpen
                ? t('teamChat.voiceTranscriptHide')
                : t('teamChat.voiceTranscriptShow')}
            </button>
          </div>
          {transcriptOpen && (
            <div className="rounded-lg border border-border bg-card px-4 py-3">
              <VoiceTranscript
                segments={segments}
                meId={me?.id}
                followLatest={Boolean(live)}
                emptyText={
                  live ? t('teamChat.voiceTranscriptEmpty') : t('teamChat.voiceCallNoTranscript')
                }
              />
            </div>
          )}
        </section>

        {/* The high-level record of the call: what was discussed, what was
            decided, who took what on. Written on request — it costs money. */}
        <VoiceCallMinutes call={call} />

        {/* Accepting a suggestion creates a task, so someone who cannot create
            tasks reads the proposals without the decide buttons. */}
        <VoiceCallTaskSuggestions
          call={call}
          readOnly={!can('tasks', 'canCreate') && !can('tasks', 'canEdit')}
        />
      </div>
    </div>
  );
}
